/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-explicit-any */
"use client"
import { Button } from "@/Components/ui/button"
import { Input } from "@/Components/ui/input"
import * as React from "react"

import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFacetedMinMaxValues,
    Column,
    Header,
    Cell,
    ColumnPinningState,
    Row,
} from "@tanstack/react-table"

import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "@/Components/ui/table"
import { Card } from "@/Components/ui/card"
import { DataTablePagination } from "./DataTablePagination"
import { DataTableViewOptions } from "./DataTableViewOptions"
import { ArrowDown, ArrowDownUp, ArrowUp, GripHorizontal } from "lucide-react"
import { DateRange } from "react-day-picker"
import { DateRangePicker } from "../ui/DateRangePicker"
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable } from "@dnd-kit/sortable"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import { CSSProperties } from "react"
import { CSS } from '@dnd-kit/utilities'
import ExportTable from "./ExportTable"
import { GroupAction } from "./groupAction"
import { useIsMobile } from "@/hooks/use-mobile"

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    setData: React.Dispatch<React.SetStateAction<TData[]>>
    loading: boolean
    viewOnly: boolean
    header_colour?: string
    table_title: string
    groupActions?: GroupAction[]
    columnVisibility?: {}
    columnDisplayNames?: [string, string][]
    passedColumnFilters?: ColumnFiltersState & { workstream?: string, impactRating?: string, status?: string, requestType?: string, supportRequestId?: number, riskId?: number, issueId?: number, projectRole?: string }
    showFilters?: boolean
    onRowClick?: (row: Row<TData>) => void
    rowSelection?: {}
    setRowSelection?: React.Dispatch<React.SetStateAction<{}>>
}

export function DataTableWrapper<TData, TValue>({
    columns,
    data,
    setData,
    loading,
    viewOnly,
    header_colour,
    table_title,
    groupActions,
    columnVisibility,
    columnDisplayNames,
    passedColumnFilters,
    showFilters = true,
    onRowClick = () => { },
    rowSelection: propRowSelection,
    setRowSelection: propSetRowSelection,
}: DataTableProps<TData, TValue>) {
    // Use internal state if not provided via props
    const [internalRowSelection, internalSetRowSelection] = React.useState({});
    const rowSelection = propRowSelection ?? internalRowSelection;
    const setRowSelection = propSetRowSelection ?? internalSetRowSelection;

    return DataTable<TData, TValue>({
        columns,
        data,
        setData,
        loading,
        viewOnly,
        header_colour,
        table_title,
        groupActions,
        columnVisibility,
        columnDisplayNames,
        passedColumnFilters,
        rowSelection,
        setRowSelection,
        showFilters,
        onRowClick,
    });
}

interface DataTableSelectionProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[]
    data: TData[]
    setData: React.Dispatch<React.SetStateAction<TData[]>>
    loading: boolean
    viewOnly: boolean
    header_colour?: string
    table_title: string
    groupActions?: GroupAction[]
    columnVisibility?: {}
    columnDisplayNames?: [string, string][]
    passedColumnFilters?: ColumnFiltersState & { workstream?: string, impactRating?: string, status?: string, requestType?: string, supportRequestId?: number, riskId?: number, issueId?: number, projectRole?: string }
    rowSelection: {}
    setRowSelection: React.Dispatch<React.SetStateAction<{}>>
    showFilters?: boolean
    onRowClick?: (row: Row<TData>) => void
}

export function DataTable<TData, TValue>({
    columns,
    data,
    setData,
    loading,
    viewOnly,
    header_colour,
    table_title,
    groupActions,
    columnVisibility,
    columnDisplayNames,
    passedColumnFilters,
    rowSelection,
    setRowSelection,
    showFilters = true,
    onRowClick = () => { },
}: DataTableSelectionProps<TData, TValue>) {
    const isMobile = useIsMobile();
    const [alternateRowColour, setAlternateRowColour] = React.useState(false);

    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
        passedColumnFilters || []
    )

    const [columnOrder, setColumnOrder] = React.useState<string[]>(
        loadColumnOrder()
    )

    const [columnPinning, setColumnPinning] = React.useState<ColumnPinningState>({
        left: ["select"],
        right: ["actions"],
    });

    const [globalFilter, setGlobalFilter] = React.useState<string>("")

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        onRowSelectionChange: setRowSelection,
        getFacetedRowModel: getFacetedRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
        getFacetedMinMaxValues: getFacetedMinMaxValues(),
        state: {
            sorting,
            columnOrder,
            columnPinning,
            columnFilters,
            globalFilter,
            rowSelection,
        },
        initialState: {
            columnVisibility: columnVisibility,
            pagination: {
                pageSize: isMobile ? 5 : 10,
            },
        },
        getRowId: (row: TData) => (row as any).id,
        onColumnOrderChange: setColumnOrder,
        onColumnPinningChange: setColumnPinning,
        onGlobalFilterChange: setGlobalFilter,
        meta: {
            updateData: (rowIndex, columnId, value) => {
                setData(old =>
                    old.map((row, index) => {
                        if (index === rowIndex) {
                            return {
                                ...old[rowIndex]!,
                                [columnId]: value,
                            }
                        }
                        return row
                    })
                )
            },
        },
    })

    React.useEffect(() => {
        saveColumnOrder();
    }, [columnOrder])

    React.useEffect(() => {
        // if data changes reset filters
        setColumnFilters(passedColumnFilters || []);
        // reset page index
        table.setPageIndex(0);
        // remove any row selection
        table.resetRowSelection();
    }, [data])

    function saveColumnOrder() {
        window.localStorage.setItem(table_title + "_columnOrder", JSON.stringify(columnOrder));
    }

    function loadColumnOrder(): string[] {
        const localStorageValue = window.localStorage.getItem(table_title + "_columnOrder");
        if (localStorageValue) {
            return JSON.parse(localStorageValue);
        }
        else {
            return columns.map(c => c.id!);
        }
    }

    function resetFilters() {
        setColumnFilters([]);
    }

    function getFormatedDataForExport() {
        let rows: Row<any>[] = table.getFilteredRowModel().rows.map((row) => {
            return {
                ...row,
                original: row.getVisibleCells().reduce((acc, cell) => {
                    if (cell.column.id === 'select' || cell.column.id === 'actions') {
                        return acc;
                    }
                    acc[cell.column.id] = cell.getValue();
                    return acc;
                }, {} as Record<string, any>)
            };
        });

        // match the row order from getSortedRowModel
        rows = table.getSortedRowModel().rows.map(sortedRow => {
            return rows.find(row => row.id === sortedRow.id) || sortedRow;
        });


        // Sort columns to match current column order
        rows = rows.map(row => {
            const sortedRow: any = {};
            columnOrder.forEach(colId => {
                if (Object.prototype.hasOwnProperty.call(row.original, colId)) {
                    sortedRow[colId] = (row.original as Record<string, any>)[colId];
                }
            });
            return { ...row, original: sortedRow };
        });

        return rows;
    }

    // reorder columns after drag & drop
    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event
        if (active && over && active.id !== over.id) {
            setColumnOrder(columnOrder => {
                const oldIndex = columnOrder.indexOf(active.id as string)
                const newIndex = columnOrder.indexOf(over.id as string)
                return arrayMove(columnOrder, oldIndex, newIndex) //this is just a splice util
            })
        }
    }

    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {})
    )

    // Mobile Card View
    if (isMobile) {
        return (
            <div className="flex flex-col w-full mb-2">
                <div className={"flex flex-col space-y-2 py-4" + (showFilters ? "" : " hidden")}>
                    <div className="flex flex-row px-2 gap-2">
                        <Input className="bg-gray-200 text-gray-800 text-xl"
                            id="globalSearch"
                            onChange={(event) => { setGlobalFilter(String(event.target.value)) }}
                            placeholder="Search..."
                        />
                    </div>
                    {columnFilters.length > 0 &&
                        <div className="flex flex-row px-2 gap-2">
                            <Button className="text-xl flex-1"
                                id="resetFilters"
                                variant="outline"
                                onClick={() => { resetFilters(); }}
                                disabled={columnFilters.length === 0}>Reset Filters</Button>
                            <ExportTable title={table_title || 'export'} header_colour={header_colour || 'bg-table-header'} rows={getFormatedDataForExport()} alternate_colours={alternateRowColour} />
                        </div>
                    }
                </div>

                <div className="flex flex-col space-y-2 p-2">
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map((row) => (
                            <Card
                                key={row.id}
                                className="p-3 space-y-2 bg-table-body text-table-text"
                                onClick={() => onRowClick(row)}
                            >
                                {row.getVisibleCells().map((cell) => {
                                    // Skip select and actions columns in card view header
                                    if (cell.column.id === 'select' || cell.column.id === 'actions') {
                                        return null;
                                    }

                                    const header = cell.column.columnDef.header;
                                    const headerText = typeof header === 'string' ? header : cell.column.id;

                                    return (
                                        <div key={cell.id} className="flex items-center border-b border-gray-700 pb-1">
                                            <div className="font-semibold text-xl text-gray-400 mr-2">{headerText}:</div>
                                            <div className="text-lg">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Render select and actions at the bottom */}
                                <div className="flex justify-between items-center pt-2">
                                    {(() => {
                                        const selectCell = row.getVisibleCells().find(cell => cell.column.id === 'select');
                                        return selectCell && (
                                            <div className="scale-150 origin-left">
                                                {flexRender(
                                                    selectCell.column.columnDef.cell,
                                                    selectCell.getContext()
                                                )}
                                            </div>
                                        );
                                    })()}
                                    {(() => {
                                        const actionsCell = row.getVisibleCells().find(cell => cell.column.id === 'actions');
                                        return actionsCell && (
                                            <div className="scale-125 origin-right">
                                                {flexRender(
                                                    actionsCell.column.columnDef.cell,
                                                    actionsCell.getContext()
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            </Card>
                        ))
                    ) : (
                        <Card className="p-6 text-center bg-table-body text-table-text">
                            {loading ? "Loading..." : "No results."}
                        </Card>
                    )}
                </div>

                <DataTablePagination table={table} groupActions={groupActions} viewOnly={viewOnly} />
            </div>
        );
    }

    return (
        // NOTE: This provider creates div elements, so don't nest inside of <table> elements
        <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToHorizontalAxis]}
            onDragEnd={handleDragEnd}
            sensors={sensors}
        >
            <div className="flex flex-col w-full">
                <div className={"flex flex-row justify-between py-4" + (showFilters ? "" : " hidden")}>
                    <DataTableViewOptions table={table} alternateRowColour={alternateRowColour} setAlternateRowColour={setAlternateRowColour} columnDisplayNames={columnDisplayNames} tableName={table_title} />
                    <Input className="mx-2 max-w-xl bg-gray-200 text-gray-800"
                        id="globalSearch"
                        onChange={(event) => { setGlobalFilter(String(event.target.value)) }}
                        placeholder="Search..."
                    />

                    <div className="flex flex-row pr-2">
                        <Button className="mx-2 h-8 lg:flex mt-1"
                            id="resetFilters"
                            variant="outline"
                            onClick={() => { resetFilters(); }}
                            disabled={columnFilters.length === 0}>Reset Filters</Button>
                        <ExportTable title={table_title || 'export'} header_colour={header_colour || 'bg-table-header'} rows={getFormatedDataForExport()} alternate_colours={alternateRowColour} />
                    </div>
                </div>
                <div className="p-2">
                    <Table className="bg-table-body text-table-text rounded-md">
                        <TableHeader className={`${header_colour ? header_colour : "bg-table-header"} text-table-header-foreground rounded-md`}>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    <SortableContext
                                        items={columnOrder}
                                        strategy={horizontalListSortingStrategy}
                                    >
                                        {headerGroup.headers.map((header) => {
                                            return (
                                                <DraggableTableHeader key={header.id} header={header} >
                                                    {header.isPlaceholder ? null : (
                                                        <div className="flex flex-col items-center">
                                                            <div
                                                                {...{
                                                                    className: header.column.getCanSort()
                                                                        ? 'flex flex-row items-center cursor-pointer select-none columnSortable'
                                                                        : '',
                                                                    onClick: header.column.getToggleSortingHandler(),
                                                                }}
                                                            >
                                                                {flexRender(
                                                                    header.column.columnDef.header,
                                                                    header.getContext()
                                                                )}
                                                                {{
                                                                    asc: <ArrowUp className="ml-2 w-4" />,
                                                                    desc: <ArrowDown className="ml-2 w-4" />,
                                                                }[header.column.getIsSorted() as string] ?? (header.column.getCanSort() ? <ArrowDownUp className="ml-2 w-4 text-muted-foreground" /> : null)}
                                                            </div>
                                                            {header.column.getCanFilter() ? (
                                                                <div className={showFilters ? "columnFilter" : "hidden"}>
                                                                    <Filter column={header.column} />
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    )}
                                                </DraggableTableHeader>
                                            )
                                        })}
                                    </SortableContext>
                                </TableRow>
                            ))}
                        </TableHeader>
                        <TableBody className={alternateRowColour ? "[&>*:nth-child(even)]:bg-table-alternate-row" : ""}>
                            {table.getRowModel().rows?.length ? (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        data-state={row?.getIsSelected() && "selected"}
                                        onClick={() => onRowClick(row)}
                                        className="tableRow"
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <SortableContext
                                                key={cell.id}
                                                items={columnOrder}
                                                strategy={horizontalListSortingStrategy}
                                            >
                                                <DragAlongCell key={cell.id} cell={cell} />
                                            </SortableContext>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center tableRow">
                                        {loading ? "Loading..." : "No results."}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </div>
                <DataTablePagination table={table} groupActions={groupActions} viewOnly={viewOnly} />
            </div>
        </DndContext>
    )
}

function Filter({ column }: { column: Column<any, unknown> }) {
    const [dateFilter, setDateFilter] = React.useState<DateRange | undefined>({
        from: undefined,
        to: undefined,
    })

    React.useEffect(() => {
        if (dateFilter?.from !== undefined && dateFilter?.to !== undefined) {
            column.setFilterValue(dateFilter);
        }
    }, [dateFilter])

    React.useEffect(() => {
        if (column.getFilterValue() === undefined) {
            setDateFilter({ from: undefined, to: undefined });
        }
    }, [column.getFilterValue()])

    const { filterVariant } = column.columnDef.meta ?? {}

    const columnFilterValue = column.getFilterValue()

    const sortedUniqueValues = React.useMemo(
        () =>
            filterVariant === 'date-range'
                ? []
                : Array.from(column.getFacetedUniqueValues().keys())
                    .sort()
                    .slice(0, 5000),
        [column.getFacetedUniqueValues(), filterVariant]
    )

    return filterVariant === 'date-range' ? (
        <div>
            <DateRangePicker
                className="mx-2"
                date={dateFilter}
                setDate={setDateFilter}
                description="Filter 'Date'"
            />
            <div className="h-1" />
        </div>
    ) : filterVariant === 'select' ? (
        <select
            className="border border-gray-400 shadow rounded text-gray-600 mx-auto text-center"
            onChange={e => column.setFilterValue(e.target.value)}
            value={columnFilterValue?.toString() || ''}
        >
            <option value=""> - </option>
            {sortedUniqueValues.map(value => (
                //dynamically generated select options from faceted values feature
                <option value={value} key={value}>
                    {value}
                </option>
            ))}
        </select>
    ) : filterVariant === 'bool-select' ? (
        <select
            className="border border-gray-400 shadow rounded text-gray-600 mx-auto text-center"
            onChange={e => column.setFilterValue(e.target.value)}
            value={columnFilterValue?.toString() || ''}
        >
            <option value="" key=""> - </option>
            <option value="true" key="true">
                True
            </option>
            <option value="false" key="false">
                False
            </option>
        </select>
    ) : (
        <>
            {/* Autocomplete suggestions from faceted values feature */}
            <datalist id={column.id + 'list'}>
                {sortedUniqueValues.map((value: any) => (
                    <option value={value} key={value} />
                ))}
            </datalist>
            <DebouncedInput
                type="text"
                value={(columnFilterValue ?? '') as string}
                onChange={value => column.setFilterValue(value)}
                //   placeholder={`Search... (${column.getFacetedUniqueValues().size})`}
                placeholder={`Search...`}
                className="w-36 border border-gray-400 text-gray-600 shadow rounded pl-2"
                list={column.id + 'list'}
            />
            <div className="h-1" />
        </>
    )
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = React.useState(initialValue)

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <input {...props} value={value} onChange={e => setValue(e.target.value)} />
    )
}

const DraggableTableHeader = ({
    header,
    children,
}: {
    header: Header<any, any>
    children: React.ReactNode
}) => {
    const { attributes, isDragging, listeners, setNodeRef, transform } =
        useSortable({
            id: header.column.id,
        })

    const style: CSSProperties = {
        opacity: isDragging ? 0.8 : 1,
        position: 'relative',
        transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
        transition: 'width transform 0.2s ease-in-out',
        whiteSpace: 'nowrap',
        padding: '0.25rem 0.5rem',
        zIndex: isDragging ? 1 : 0,
        color: 'hsl(var(--table-header-foreground))',
    }

    return (
        <th colSpan={header.colSpan} ref={setNodeRef} style={style}>
            {header.column.id !== 'select' && header.column.id !== 'actions' && (
                <button {...attributes} {...listeners}>
                    <GripHorizontal className="h-3 m-0 p-0 columnDraggable" />
                </button>
            )}
            {header.isPlaceholder ? null : children}
        </th>
    )
}

const DragAlongCell = ({ cell }: { cell: Cell<any, any> }) => {
    const { isDragging, setNodeRef, transform } = useSortable({
        id: cell.column.id,
    })

    const style: CSSProperties = {
        opacity: isDragging ? 0.8 : 1,
        position: 'relative',
        transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
        transition: 'width transform 0.2s ease-in-out',
        padding: '0.5rem',
        zIndex: isDragging ? 1 : 0,
    }

    return (
        <td style={style} ref={setNodeRef}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </td>
    )
}